export interface FilterCount {
  count: number;
  activatedCount: number;
}

export interface VisibleFilterItem {
  key: string;
  filter: FilterItem;
}

type FlexibleListFilter = ListFilter & Record<string, any>;

import { CommonModule } from '@angular/common';
import { Component, computed, effect, EventEmitter, Input, Output, Signal, signal } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FilterItem } from '../../models/filter-item';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ListFilter } from '../../models/list-filter';
import { LoggingApplicationInsightsService } from '../../services/logging-application-insights/logging-application-insights.service';
import { SortCoordinationService } from '../../services/sort-coordination/sort-coordination.service';

@Component({
  selector: 'app-filter-input-chips',
  imports: [CommonModule,
    MatChipsModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatTooltipModule],
  templateUrl: './filter-input-chips.component.html',
  styleUrl: './filter-input-chips.component.scss',
  standalone: true
})

export class FilterInputChipsComponent {
  MAX_VISIBLE: number | undefined = 20;

  @Input()
  set selectedValues(listFilter: FlexibleListFilter) {
    this.selectedFilterValues.set(listFilter);
  }
  @Input() tableId?: string;
  @Output() selectedValuesChange = new EventEmitter<FlexibleListFilter>();

  filterIsActivated: boolean = true;
  selectedFilterValues = signal<FlexibleListFilter>({ search: [] });
  filterCount: Signal<FilterCount> = computed(() => this.getTotalFilterCount());
  visibleChips: Signal<VisibleFilterItem[]> = computed(() => {
    return this.getKeys()
      .flatMap(key =>
        this.selectedFilterValues()[key].map((filter: FilterItem) => ({ key, filter }))
      )
      .slice(0, this.MAX_VISIBLE);
  })
  remainingCount: Signal<number> = computed(() => this.filterCount().count - this.visibleChips().length);

  constructor(
    private loggingApplicationInsights: LoggingApplicationInsightsService,
    private sortCoordinationService: SortCoordinationService
  ) {
    effect((): void => {
      this.filterIsActivated = this.isAnyChipsActivated(this.filterCount());
    });
  }

  private getKeys() {
    const allKeys = Object.keys(this.selectedFilterValues());

    return allKeys;
  }

  private getTotalFilterCount(): FilterCount {
    let filterCount = 0;
    let activatedFilterCount = 0;
    for (const key of this.getKeys()) {
      filterCount += this.selectedFilterValues()[key].length;
      activatedFilterCount += this.selectedFilterValues()[key].filter((f: FilterItem) => f.activated).length
    }

    return {
      count: filterCount,
      activatedCount: activatedFilterCount
    };
  }

  private isAnyChipsActivated(filterCount: FilterCount): boolean {
    if (filterCount.activatedCount) {
      return true;
    }

    return false;
  }

  toggleChipsFilter(filter: FilterItem) {
    filter.activated = !filter.activated;
    console.log('Toggled filter:', filter);

    this.selectedFilterValues.set({ ...this.selectedFilterValues() });
    this.selectedValuesChange.emit(this.selectedFilterValues());
  }

  toggleAllChipsFilters($event: MatSlideToggleChange) {
    this.loggingApplicationInsights.logEvent(`toggleAllChipsFilters`, { origin: this.constructor.name });

    this.filterIsActivated = $event.checked;

    for (const key of this.getKeys()) {
      for (const selectedValue of this.selectedFilterValues()[key]) {
        selectedValue.activated = this.filterIsActivated;
      }
    }

    this.selectedFilterValues.set({ ...this.selectedFilterValues() });
    this.selectedValuesChange.emit(this.selectedFilterValues());
  }

  removeFilter(filter: FilterItem, key: string) {
    if (this.selectedFilterValues()[key].includes(filter)) {
      this.selectedFilterValues()[key] = this.selectedFilterValues()[key].filter((f: FilterItem) => f.value !== filter.value);
    }

    this.selectedFilterValues.set({ ...this.selectedFilterValues() });
    this.selectedValuesChange.emit(this.selectedFilterValues());
  }

  removeSearch(filter: FilterItem) {
    filter.value = '';
  }

  removeAllFilters() {
    for (const key of this.getKeys()) {
      this.selectedFilterValues()[key] = [];
    }

    this.filterIsActivated = true;
    this.selectedFilterValues.set({ ...this.selectedFilterValues() });
    this.selectedValuesChange.emit(this.selectedFilterValues());

    if (this.tableId) {
      this.sortCoordinationService.clearSortForTable(this.tableId);
    }
  }
}
